import { filterAvailableModules } from '@utils/courseModules'
import { ICourseContentsNodes, ICourseModulesNodes } from '@dtos/Course'
import { IEnrollment } from '@dtos/Enrollment'
import { isContentAvailable } from '@utils/contentAvailable'

export type ModuleTargetLesson =
  | {
      kind: 'lesson'
      content: ICourseContentsNodes
      moduleId: number
      parentCourseModuleId?: number
    }
  | { kind: 'blocked'; reason: 'date' | 'linear' }
  | { kind: 'empty' }

interface IModuleTargetParams {
  module: ICourseModulesNodes
  enrollment: IEnrollment
  isContentBlocked: (courseContentId: number | string) => boolean
}

interface IResolveModuleTargetParams extends IModuleTargetParams {
  fetchMoreContents?: (
    moduleId: number,
    offset: number,
  ) => Promise<ICourseContentsNodes[] | undefined | null>
}

interface ICandidate {
  content: ICourseContentsNodes
  moduleId: number
  parentCourseModuleId?: number
}

interface IScope {
  module: ICourseModulesNodes
  parentCourseModuleId?: number
}

// Mesma ordem da tela do curso: o próprio módulo e depois os submódulos
// disponíveis, cada um com as aulas na ordem do slider.
const scopesOf = (module: ICourseModulesNodes): IScope[] => [
  { module },
  ...filterAvailableModules(module.courseModules || []).map((submodule) => ({
    module: submodule,
    parentCourseModuleId: submodule.parentCourseModule?.id ?? module.id,
  })),
]

// Conteúdo que a tela do curso não deixa abrir de jeito nenhum (escondido pelo
// servidor ou card vazio, sem mídia nem atividade) não é candidato.
const isPlayable = (content: ICourseContentsNodes) =>
  content.available !== false &&
  (!!content.lesson.media || !!content.lesson.activity)

const candidatesOf = (
  scope: IScope,
  contents: ICourseContentsNodes[],
): ICandidate[] =>
  contents.filter(isPlayable).map((content) => ({
    content,
    moduleId: scope.module.id,
    parentCourseModuleId: scope.parentCourseModuleId,
  }))

const hasMorePages = (module: ICourseModulesNodes, loaded: number) =>
  loaded < (module.courseContentsCount ?? 0)

function classify(
  candidate: ICandidate,
  { enrollment, isContentBlocked }: Omit<IModuleTargetParams, 'module'>,
) {
  const { lesson } = candidate.content
  const isReleased = isContentAvailable({
    releaseAt: lesson.releaseAt,
    releaseAfter: lesson.releaseAfter,
    lockAt: lesson.lockAt,
    lockAfter: lesson.lockAfter,
    createdAt: enrollment.activatedAt,
    courseClassRelease: enrollment.courseClassRelease,
    courseClassReleaseOffset: lesson.courseClassReleaseOffset,
  })
  if (!isReleased) return 'date' as const
  if (isContentBlocked(candidate.content.id)) return 'linear' as const
  return 'open' as const
}

const isCompleted = (candidate: ICandidate, enrollment: IEnrollment) =>
  enrollment.progressCard?.lessonsProgresses?.some(
    (progress) =>
      progress.lessonId === candidate.content.lesson.id && progress.completed,
  ) ?? false

const toLesson = (candidate: ICandidate): ModuleTargetLesson => ({
  kind: 'lesson',
  content: candidate.content,
  moduleId: candidate.moduleId,
  parentCourseModuleId: candidate.parentCourseModuleId,
})

// Próxima aula não concluída na ordem da tela. Se ela estiver travada (data ou
// progresso linear), o card mostra o bloqueio em vez de pular para a seguinte,
// como a tela do curso faria.
function nextNotCompleted(
  candidates: ICandidate[],
  params: Omit<IModuleTargetParams, 'module'>,
): ModuleTargetLesson | null {
  const next = candidates.find(
    (candidate) => !isCompleted(candidate, params.enrollment),
  )
  if (!next) return null

  const status = classify(next, params)
  if (status === 'open') return toLesson(next)
  return { kind: 'blocked', reason: status }
}

// Tudo concluído: volta para a primeira aula que ainda abre.
function firstOpen(
  candidates: ICandidate[],
  params: Omit<IModuleTargetParams, 'module'>,
): ModuleTargetLesson {
  if (candidates.length === 0) return { kind: 'empty' }

  let firstBlockedReason: 'date' | 'linear' | null = null
  for (const candidate of candidates) {
    const status = classify(candidate, params)
    if (status === 'open') return toLesson(candidate)
    firstBlockedReason ??= status
  }
  return { kind: 'blocked', reason: firstBlockedReason ?? 'date' }
}

/**
 * Aula de destino do card de módulo da home focada, só com o que já está
 * carregado. `unresolved` quando todas as aulas carregadas estão concluídas
 * mas o módulo ainda tem páginas: aí só `resolveModuleTargetLesson` decide.
 */
export function moduleTargetFromLoaded({
  module,
  ...params
}: IModuleTargetParams): ModuleTargetLesson | { kind: 'unresolved' } {
  const scopes = scopesOf(module)
  const candidates = scopes.flatMap((scope) =>
    candidatesOf(scope, scope.module.courseContents?.nodes ?? []),
  )

  const next = nextNotCompleted(candidates, params)
  if (next) return next

  const isComplete = scopes.every(
    (scope) =>
      !hasMorePages(
        scope.module,
        scope.module.courseContents?.nodes.length ?? 0,
      ),
  )
  if (!isComplete) return { kind: 'unresolved' }

  return firstOpen(candidates, params)
}

/**
 * Aula de destino do card de módulo (FR-017): a próxima não concluída e
 * disponível, na ordem da tela do curso, incluindo submódulos; com tudo
 * concluído, a primeira disponível. Busca mais páginas de conteúdo só quando
 * as carregadas não resolvem.
 */
export async function resolveModuleTargetLesson({
  module,
  fetchMoreContents,
  ...params
}: IResolveModuleTargetParams): Promise<ModuleTargetLesson> {
  const candidates: ICandidate[] = []

  for (const scope of scopesOf(module)) {
    let contents = scope.module.courseContents?.nodes ?? []
    candidates.push(...candidatesOf(scope, contents))

    let next = nextNotCompleted(candidates, params)
    if (next) return next
    if (!fetchMoreContents) continue

    while (hasMorePages(scope.module, contents.length)) {
      const page = await fetchMoreContents(scope.module.id, contents.length)
      if (!page?.length) break

      contents = [...contents, ...page]
      candidates.push(...candidatesOf(scope, page))

      next = nextNotCompleted(candidates, params)
      if (next) return next
    }
  }

  return firstOpen(candidates, params)
}
